import { apiGet, apiPost, apiPut, apiDelete } from './client';
import type { ApiResponse, PagedResponse, SearchParams } from './types';

// --- Product Document ---

export interface Product {
  id: string;
  tenant_id: string;
  name: string;
  description: string;
  sku: string;
  category: string;
  type: string;
  unit_price: number;
  currency: string;
  cost_price: number;
  tax_rate: number;
  status: string;
  stock_quantity: number;
  reorder_level: number;
  unit_of_measure: string;
  weight: number;
  dimensions: string;
  images: string[];
  specifications: Record<string, string>;
  supplier_id: string;
  tags: string[];
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Inventory Transaction Document ---

export interface InventoryTransaction {
  id: string;
  tenant_id: string;
  product_id: string;
  type: string;
  quantity: number;
  reference_type: string;
  reference_id: string;
  notes: string;
  warehouse_id: string;
  branch_id: string;
  created_by: string;
  updated_by: string;
  created_at: string;
  updated_at: string;
}

// --- Stock Info ---

export interface StockInfo {
  product_id: string;
  product_name: string;
  sku: string;
  stock_quantity: number;
  reorder_level: number;
  needs_reorder: boolean;
  unit_of_measure: string;
}

// --- Create/Update DTOs ---

export interface CreateProductDto {
  name: string;
  description?: string;
  sku: string;
  category?: string;
  type: string;
  unit_price: number;
  currency?: string;
  cost_price: number;
  tax_rate: number;
  stock_quantity: number;
  reorder_level: number;
  unit_of_measure?: string;
  weight: number;
  dimensions?: string;
  images?: string[];
  specifications?: Record<string, string>;
  supplier_id?: string;
  tags?: string[];
}

export interface UpdateProductDto {
  name: string;
  description?: string;
  sku: string;
  category?: string;
  type: string;
  unit_price: number;
  currency?: string;
  cost_price: number;
  tax_rate: number;
  status?: string;
  reorder_level: number;
  unit_of_measure?: string;
  weight: number;
  dimensions?: string;
  images?: string[];
  specifications?: Record<string, string>;
  supplier_id?: string;
  tags?: string[];
}

export interface CreateInventoryTransactionDto {
  product_id: string;
  type: string;
  quantity: number;
  reference_type: string;
  reference_id?: string;
  notes?: string;
  warehouse_id?: string;
}

// --- Filter Types ---

export interface ProductFilters extends SearchParams {
  type?: string;
  status?: string;
  category?: string;
  supplier_id?: string;
}

export interface InventoryTransactionFilters extends SearchParams {
  product_id?: string;
  type?: string;
  reference_type?: string;
  warehouse_id?: string;
}

// ========== Product API Functions ==========

export async function getProducts(params?: ProductFilters) {
  return apiGet<PagedResponse<Product>>('/api/v1/products', params);
}

export async function getProduct(id: string) {
  return apiGet<Product>(`/api/v1/products/${id}`);
}

export async function createProduct(data: CreateProductDto) {
  return apiPost<Product>('/api/v1/products', data);
}

export async function updateProduct(id: string, data: UpdateProductDto) {
  return apiPut<Product>(`/api/v1/products/${id}`, data);
}

export async function deleteProduct(id: string) {
  return apiDelete<void>(`/api/v1/products/${id}`);
}

// ========== Stock Queries ==========

export async function getProductStock(id: string) {
  return apiGet<StockInfo>(`/api/v1/products/${id}/stock`);
}

export async function getLowStockProducts(params?: { from?: number; size?: number }) {
  return apiGet<PagedResponse<Product>>('/api/v1/products/low-stock', params);
}

// ========== Inventory Transaction API Functions ==========

export async function getInventoryTransactions(params?: InventoryTransactionFilters) {
  return apiGet<PagedResponse<InventoryTransaction>>('/api/v1/inventory-transactions', params);
}

export async function getInventoryTransaction(id: string) {
  return apiGet<InventoryTransaction>(`/api/v1/inventory-transactions/${id}`);
}

export async function createInventoryTransaction(data: CreateInventoryTransactionDto) {
  return apiPost<InventoryTransaction>('/api/v1/inventory-transactions', data);
}

export async function deleteInventoryTransaction(id: string) {
  return apiDelete<void>(`/api/v1/inventory-transactions/${id}`);
}
